import type { ITenant } from '@experiences/interfaces';
import { UiStack } from '@experiences/ui-common';
import {
    ApDropdown,
    ApDropdownItem,
} from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useEffect,
    useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import type { Folder } from '../../../../services/orchestrator/OrchFolderService';
import {
    FolderType,
    getAllFoldersForCurrentUser,
} from '../../../../services/orchestrator/OrchFolderService';
import { accountGlobalId } from '../../../../store/selectors';
import {
    LabelWithTooltip,
    type TooltipProps,
} from './LabelWithTooltip';

interface FolderDropdownProps {
    tenant?: ITenant;
    value?: Folder;
    required?: boolean;
    disableAutoSelectFirstOption?: boolean;
    onChange: (newFolder?: Folder) => void;
    labelTooltip?: TooltipProps;
}

interface InnerDropdownProps {
    tenant?: ITenant;
    isFoldersLoading: boolean;
    availableFolders?: Folder[];
    selectedValue?: string;
    handleOnChange: (event: CustomEvent) => void;
    required?: boolean;
    label?: string;
}

const InnerDropdown: React.FC<InnerDropdownProps> = ({
    tenant, isFoldersLoading, availableFolders, selectedValue, handleOnChange, required, label,
}: InnerDropdownProps) => (
    <ApDropdown
        style={{ width: '300px' }}
        label={label}
        required={required}
        selectedValue={selectedValue}
        onSelectedValueChanged={handleOnChange}
        data-cy="llm-configurations-folder-dropdown"
        disabled={!tenant?.id || isFoldersLoading}>
        {availableFolders?.map((folder) => (
            <ApDropdownItem
                value={folder.Key}
                label={folder.FullyQualifiedName}
                key={`ap-dropdown-item-${folder.Key}`}
                data-cy={`ap-dropdown-item-${folder.Key}`} />
        ))}
    </ApDropdown>
);

export const FolderDropdown: React.FC<FolderDropdownProps> = ({
    tenant, value, required, disableAutoSelectFirstOption, onChange, labelTooltip,
}: FolderDropdownProps) => {
    const { formatMessage: translate } = useIntl();
    const organizationId = useSelector(accountGlobalId);

    const [ selectedValue, setSelectedValue ] = useState<string | undefined>(value?.Key);

    const {
        data: availableFolders, isValidating: isFoldersLoading,
    } = useSWR(
        tenant?.id ? [ organizationId, tenant?.id ] : undefined,
        () => getAllFoldersForCurrentUser({
            accountLogicalName: organizationId,
            tenantName: tenant?.id ?? '',
        }).then((response) => response.PageItems.filter(f => f.FolderType !== FolderType.Personal && (f.FolderType as string) !== 'DebugSolution')),
    );

    const handleOnChange = useCallback((event: CustomEvent) => {
        const newValue = event.detail as string;
        const folder = availableFolders?.find((f) => f.Key === newValue);
        setSelectedValue(newValue);
        onChange(folder);
    }, [ onChange, availableFolders ]);

    useEffect(() => {
        // Set the first folder as the default value if no folder is provided
        if (!disableAutoSelectFirstOption && !selectedValue && availableFolders && availableFolders.length > 0) {
            const defaultFolder = availableFolders[0];
            setSelectedValue(defaultFolder.Key);
            onChange(defaultFolder);
        }
    }, [ selectedValue, availableFolders, onChange, disableAutoSelectFirstOption ]);

    useEffect(() => {
        // Update the selected value when the prop value changes
        setSelectedValue(value?.Key);
    }, [ value ]);

    const tooltipConfig: TooltipProps = {
        translationKey: 'CLIENT_BYO_LLM_TOOLTIP_CONNECTIONS_FOLDER',
        placement: 'top',
    };

    return (
        <UiStack direction='column'>
            <LabelWithTooltip
                label={translate({ id: 'CLIENT_BYO_LLM_CONNECTIONS_FOLDER' })}
                required={required}
                tooltip={labelTooltip ?? tooltipConfig}
                dataCy="llm-configurations-folder"
            />
            <InnerDropdown
                tenant={tenant}
                isFoldersLoading={isFoldersLoading}
                availableFolders={availableFolders}
                selectedValue={selectedValue}
                handleOnChange={handleOnChange}
                required={required}
            />
        </UiStack>
    );
};
